/**
 * RequestBetaUpgradeModal
 *
 * Globally mounted modal that listens for the `credit-limit-reached`
 * window event (broadcast by the axios 402 interceptor in
 * src/api/base/api.js) and pops a dialog.
 *
 * Two variants based on payload.scope:
 *   - 'org'      -> "You've used all your monthly Evaligo credits.
 *                    Request a beta upgrade to keep going."  (textarea)
 *   - 'platform' -> "ClientCues has hit its monthly capacity. We've
 *                    been notified."  (no action — informational only)
 *
 * Mount once in App.js (or any always-mounted layout). No props.
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { MdClose, MdBolt, MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { requestBetaUpgrade, getCreditUsage } from '../../../api/intelligenceRequests';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const popIn  = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
`;

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, .55);
  backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} .15s ease both;
  padding: 1rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 25px 60px -10px rgba(15, 23, 42, .35);
  animation: ${popIn} .18s ease both;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex; align-items: center; gap: .75rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  .icon {
    width: 38px; height: 38px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    background: ${p => (p.scope === 'platform'
      ? 'linear-gradient(135deg, #fef3c7, #fde68a)'
      : 'linear-gradient(135deg, #eef2ff, #c7d2fe)')};
    color: ${p => (p.scope === 'platform' ? '#92400e' : '#4338ca')};
    svg { font-size: 20px; }
  }
  h3 {
    margin: 0;
    font-size: 1.05rem;
    color: #0f172a;
    font-weight: 700;
  }
  .sub {
    margin-top: .15rem;
    font-size: .82rem;
    color: #64748b;
  }
  .close {
    margin-left: auto;
    background: transparent; border: none; cursor: pointer;
    color: #94a3b8;
    width: 32px; height: 32px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    &:hover { background: #f1f5f9; color: #0f172a; }
  }
`;

const Body = styled.div`
  padding: 1.25rem 1.5rem;
  p { margin: 0 0 .8rem; color: #334155; line-height: 1.45; font-size: .92rem; }
  .meta {
    display: flex; gap: 1rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: .65rem .85rem;
    margin: .25rem 0 1rem;
    font-size: .82rem;
    color: #475569;
    span { white-space: nowrap; }
    b { color: #0f172a; font-weight: 700; }
  }
  textarea {
    width: 100%;
    min-height: 90px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    padding: .65rem .8rem;
    font-family: inherit;
    font-size: .9rem;
    resize: vertical;
    color: #0f172a;
    &:focus { outline: none; border-color: #6366f1; }
    &::placeholder { color: #94a3b8; }
  }
  .cooldown {
    background: #fef9c3;
    border: 1px solid #fde68a;
    color: #854d0e;
    padding: .55rem .75rem;
    border-radius: 8px;
    font-size: .82rem;
    margin-bottom: .5rem;
  }
`;

const Footer = styled.div`
  padding: 1rem 1.5rem 1.25rem;
  display: flex; gap: .6rem; justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  background: #fbfcfd;
  button {
    border: none;
    border-radius: 9px;
    padding: .55rem 1rem;
    font-weight: 600;
    font-size: .88rem;
    cursor: pointer;
    transition: background .15s, transform .1s;
    &:active { transform: translateY(1px); }
  }
  .secondary {
    background: #fff;
    color: #475569;
    border: 1.5px solid #e2e8f0;
    &:hover { background: #f8fafc; }
  }
  .primary {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff;
    &:hover { filter: brightness(1.05); }
    &:disabled { opacity: .55; cursor: not-allowed; filter: none; }
  }
`;

export default function RequestBetaUpgradeModal() {
  const [open, setOpen]       = useState(false);
  const [payload, setPayload] = useState(null);
  const [reason, setReason]   = useState('');
  const [usage, setUsage]     = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setPayload(null);
    setReason('');
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const data = event.detail || {};
      setPayload(data);
      setOpen(true);
      // Pull a fresh usage snapshot so we can show the cooldown state
      // and recent-usage line. Best-effort — modal still works without it.
      getCreditUsage()
        .then(res => setUsage(res?.data || res))
        .catch(() => setUsage(null));
    };
    window.addEventListener('credit-limit-reached', handler);
    return () => window.removeEventListener('credit-limit-reached', handler);
  }, []);

  if (!open || !payload) return null;

  const isPlatform = payload.scope === 'platform';
  // Proactive = opened from the budget meter / plan area, NOT because a call
  // was blocked. Softer copy (no scary "you've hit your limit").
  const proactive = !!payload.proactive && !isPlatform;
  const cooldownActive  = usage && usage.can_request_upgrade === false;
  const cooldownDays    = usage?.days_until_can_request || 0;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await requestBetaUpgrade(reason);
      const body = res?.data || res || {};
      toast.success(body.message
        || 'Thanks. We\'ll get back to you within a few business days.');
      close();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      if (status === 429) {
        toast.warn(data.message
          || `You can request another upgrade in ${data.days_remaining || '?'} day(s).`);
      } else {
        toast.error('Could not submit request. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Backdrop onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <Card>
        <Header scope={payload.scope}>
          <div className="icon">
            {isPlatform ? <MdInfoOutline /> : <MdBolt />}
          </div>
          <div>
            <h3>{isPlatform
              ? 'ClientCues is at monthly capacity'
              : proactive
                ? 'Add budget'
                : "You've reached your monthly limit"}</h3>
            <div className="sub">
              {isPlatform
                ? 'Try again next month — we\'ve been notified.'
                : proactive
                  ? 'Ask us to raise your monthly budget — it\'s free.'
                  : 'You\'ve hit your monthly AI usage cap.'}
            </div>
          </div>
          <button className="close" onClick={close} aria-label="Close">
            <MdClose />
          </button>
        </Header>

        <Body>
          {isPlatform ? (
            <p>
              The platform has hit its monthly AI budget. We've
              been notified and will lift the limit shortly. Your account
              isn't affected — you can continue once the next calendar
              month rolls over (or sooner if we raise the platform cap).
            </p>
          ) : (
            <>
              <p>
                Tell us a bit about what you're working on and we'll
                review your account for a free upgrade. We typically
                respond within a few business days.
              </p>
              {usage && (() => {
                const usd = (v) => `$${Number(v || 0).toFixed(2)}`;
                const spent = usd(usage.total_spend_usd);
                const cap = Number(usage.budget_usd_cap) === -1
                  ? '∞' : usd(usage.budget_usd_cap);
                return (
                  <div className="meta">
                    <span>Plan: <b>{usage.plan_name || 'free'}</b></span>
                    <span>Used: <b>{spent} / {cap}</b></span>
                    {payload.flow_type && (
                      <span>Blocked flow: <b>{payload.flow_type}</b></span>
                    )}
                  </div>
                );
              })()}
              {cooldownActive ? (
                <div className="cooldown">
                  You can submit another request in {cooldownDays} day(s).
                </div>
              ) : (
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="What are you trying to do? (helps us prioritise)"
                  maxLength={4000}
                />
              )}
            </>
          )}
        </Body>

        <Footer>
          <button className="secondary" onClick={close}>
            {isPlatform ? 'Got it' : 'Not now'}
          </button>
          {!isPlatform && (
            <button
              className="primary"
              onClick={submit}
              disabled={submitting || cooldownActive}
            >
              {submitting ? 'Sending…' : 'Request free upgrade'}
            </button>
          )}
        </Footer>
      </Card>
    </Backdrop>
  );
}
